import Checkbox from "@mui/material/Checkbox";
import type {ColumnDef, Row, RowData, Table as TanStackTable} from "@tanstack/react-table";
import {useTranslation} from "react-i18next";

export const SERVER_TABLE_SELECTION_COLUMN_ID = "__selection";

const ServerTableSelectionHeader = <TData extends RowData>({
    table
}: {
    table: TanStackTable<TData>
}) => {
    const {t} = useTranslation();
    const hasSelectableRows = table.getRowModel().rows.some(row => row.getCanSelect());

    return (
        <Checkbox
            size="small"
            checked={hasSelectableRows && table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            disabled={!hasSelectableRows}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
            inputProps={{'aria-label': t('Select all rows on this page')}}
            sx={{p: 0.5}}
        />
    );
};

const ServerTableSelectionCell = <TData extends RowData>({row}: {row: Row<TData>}) => {
    const {t} = useTranslation();

    return (
        <Checkbox
            data-no-row-click
            size="small"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            inputProps={{'aria-label': t('Select row')}}
            sx={{p: 0.5}}
        />
    );
};

export const createServerTableSelectionColumn = <TData extends RowData>(): ColumnDef<TData, unknown> => ({
    id: SERVER_TABLE_SELECTION_COLUMN_ID,
    header: ({table}) => <ServerTableSelectionHeader table={table}/>,
    cell: ({row}) => <ServerTableSelectionCell row={row}/>,
    size: 44,
    minSize: 44,
    maxSize: 44,
    enablePinning: false,
    enableResizing: false,
    enableSorting: false,
    meta: {align: 'center'}
});
